import Button from 'react-bootstrap/Button'
import  React from 'react'
import { MarketShare } from '../../../../models/MarketShare'
import { Option } from '../../../../models/Option'
import { ISupportSpinner } from '../../../../shared/Ajax'
import { Backdrop } from '../../../../shared/Backdrop'
import { MoneyField, TextSelectField, validateFields } from '../../../../shared/fields'
import { PercentageField } from '../../../../shared/fields/PercentageField'
import { ShowIf } from '../../../../shared/fields/ShowIf'
import { ISection, TableColumn } from '../../../../shared/table'
import {
  getFY, getMarketString, newGuid, formatNumber, getPercentage, formatMoneyForDisplay,
} from '../../../../shared/Utility'

export interface AllocationTotals {
    allocatedExpenses: number
    allocatedEarnings: number
}

export const calculateAllocations = (values: MarketShare[]): AllocationTotals => {
  let allocatedExpenses = 0
  let allocatedEarnings = 0

  if (values.length > 0) {
    values.forEach((x) => {
      allocatedExpenses += x.expenses
      allocatedEarnings += x.earnings
    })
  }

  return {
    allocatedExpenses,
    allocatedEarnings,
  }
}

export const marketShareColumns = (markets: Option<string>[], expenseTotal: number, earningTotal: number, claimYear: number): TableColumn<MarketShare>[] => [
  {
    name: 'Country',
    content: (d) => (d.countryLabel ? d.countryLabel : getMarketString([d.marketId], markets)),
    responsive: 'md',
  },
  {
    name: 'Expenses A$',
    content: (d) => <span style={{ color: d.expenses < 0 ? 'red' : '' }}>{formatMoneyForDisplay(d.expenses)}</span>,
  },
  {
    name: 'Expenses %',
    content: (d) => <span style={{ color: d.expenses < 0 ? 'red' : '' }}>{formatNumber(getPercentage(d.expenses, expenseTotal) * 100)}</span>,
  },
  {
    name: `Earnings A$ ${getFY(claimYear)}`,
    content: (d) => <span style={{ color: d.earnings < 0 ? 'red' : '' }}>{formatMoneyForDisplay(d.earnings)}</span>,
  },
  {
    name: `Earnings % ${getFY(claimYear)}`,
    content: (d) => <span style={{ color: d.earnings < 0 ? 'red' : '' }}>{formatNumber(getPercentage(d.earnings, earningTotal) * 100)}</span>,
  },
]

export const marketShareSummaryRows = (values: MarketShare[], expenseTotal: number, earningTotal: number): MarketShare[] => {
  const summaryRows: MarketShare[] = []
  if (values.length > 0) {
    const allocations = calculateAllocations(values)

    summaryRows.push({
      id: 'RestOfTheWorld', marketId: '', countryLabel: 'Rest of the world', expenses: expenseTotal - allocations.allocatedExpenses, earnings: earningTotal - allocations.allocatedEarnings,
    })
  }
  summaryRows.push({
    id: 'Total', marketId: '', countryLabel: 'Total', expenses: expenseTotal, earnings: earningTotal,
  })

  return summaryRows
}

export interface MarketShareDetailsProps {
    id: string
    model: MarketShare
    markets: Option<string>[]
    marketsAlreadyAllocated: MarketShare[]
    expenseTotal: number
    earningTotal: number
    claimYear: number
    onChange: { (v: MarketShare, close: boolean): any }
    onClose: { (): any }
    onRemove: { (): any }
    preview?: boolean
}

export interface MarketShareDetailsState {
    model: MarketShare
    loading: boolean
    validated: boolean
}

export class MarketShareDetails extends React.Component<MarketShareDetailsProps, MarketShareDetailsState> implements ISection, ISupportSpinner {
    market: TextSelectField
    expenseAmount: MoneyField
    expensePercentage: PercentageField
    earningAmount: MoneyField
    earningPercentage: PercentageField

    constructor(props: MarketShareDetailsProps) {
      super(props)

      const model = this.props.model ? { ...this.props.model } : {
        id: newGuid(),
        marketId: '',
        countryLabel: '',
        expenses: null,
        earnings: null,
      }

      this.state = {
        model,
        loading: false,
        validated: false,
      }
    }

    toggleSpinner(on: boolean, callback: { (): any }): void {
      this.setState({ loading: on }, callback)
    }

    validate = (callback: { (res: boolean): any }) => {
      this.setState({ validated: true }, () => {
        validateFields((res) => this.onChange({ }, () => callback(res)),
          this.market,
          this.expenseAmount,
          this.expensePercentage,
          this.earningAmount,
          this.earningPercentage)
      })
    }

    saveAndClose = (callback?: { (res: boolean): any }) => {
      if (this.state.loading) {
        callback && callback(false)
        return
      }
      this.validate((res) => {
        if (res) {
          this.props.onChange(this.state.model, true)
        }
        callback && callback(res)
      })
    }

    onChange = (change: Partial<MarketShare>, callback?: { (): any }) => {
      this.setState({
        model: { ...this.state.model, ...change },
      }, callback)
    }

    onExpensePercentageChange = (val: number) => {
      const newExpenses = Math.round(val * this.props.expenseTotal)
      const currentExpenses = this.state.model.expenses || 0

      const percentageChange = (Math.abs(newExpenses - currentExpenses) / this.props.expenseTotal) * 100
      if (percentageChange >= 0.01) // To cater for amount being changed due to % filed rounding
      { this.onChange({ expenses: newExpenses }) }
    }

    onEarningPercentageChange = (val: number) => {
      const newEarnings = Math.round(val * this.props.earningTotal)
      const currentEarnings = this.state.model.earnings || 0

      const percentageChange = (Math.abs(newEarnings - currentEarnings) / this.props.earningTotal) * 100
      if (percentageChange >= 0.01) // To cater for amount being changed due to % filed rounding
      { this.onChange({ earnings: newEarnings }) }
    }

    validateExpense(): string {
      if (this.state.validated && !this.state.model.expenses) { return "Please complete at least one of 'Expenses $A' and 'Expenses %'" }

      return ''
    }

    validateEarning(): string {
      if (this.state.validated && !this.state.model.expenses) { return `Please complete at least one of 'Earnings $A ${getFY(this.props.claimYear)} only' and 'Earnings % ${getFY(this.props.claimYear)} only'` }

      return ''
    }

    render() {
      const { model } = this.state

      const expensesPercentage = model.expenses != null ? getPercentage(model.expenses, this.props.expenseTotal) : null
      const earningsPercentage = model.earnings != null ? getPercentage(model.earnings, this.props.earningTotal) : null

      return (
        <>
          <TextSelectField
            ref={(r) => this.market = r}
            id={`${this.props.id}-market`}
            label="Country"
            value={model.marketId}
            onChange={(marketId) => this.onChange({ marketId })}
            dataSource={this.props.markets.filter((m) => !this.props.marketsAlreadyAllocated.some((ms) => ms.marketId == m.value) || m.value == model.marketId)}
            required
            preview={this.props.preview}
          />
          <div className="clearfix">
            <MoneyField
              ref={(r) => this.expenseAmount = r}
              className="float-left mr-3"
              id={`${this.props.id}-expenseAmount`}
              label="Expenses $A (per country)"
              value={model.expenses}
              onChange={(expenses) => this.onChange({ expenses })}
              preview={this.props.preview}
              errorMessage={this.validateExpense()}
            />
            <PercentageField
              ref={(r) => this.expensePercentage = r}
              className="float-left mr-3"
              id={`${this.props.id}-expensePercentage`}
              label="Expenses % (per country)"
              value={expensesPercentage}
              onChange={(expensesPercentage) => this.onExpensePercentageChange(expensesPercentage)}
              preview={this.props.preview}
              errorMessage={this.validateExpense()}
            />
          </div>
          <div className="clearfix">
            <MoneyField
              ref={(r) => this.earningAmount = r}
              className="float-left mr-3"
              id={`${this.props.id}-earningAmount`}
              label={`Earnings $A ${getFY(this.props.claimYear)} only (per country)`}
              value={model.earnings}
              onChange={(earnings) => this.onChange({ earnings })}
              preview={this.props.preview}
              allowNegative
              errorMessage={this.validateEarning()}
            />
            <PercentageField
              ref={(r) => this.earningPercentage = r}
              className="float-left mr-3"
              id={`${this.props.id}-earningPercentage`}
              label={`Earnings % ${getFY(this.props.claimYear)} only (per country)`}
              value={earningsPercentage}
              onChange={(earningsPercentage) => this.onEarningPercentageChange(earningsPercentage)}
              preview={this.props.preview}
              errorMessage={this.validateEarning()}
            />
          </div>

          <ShowIf condition={!this.props.preview} className="clearfix mt-3">
            <Button className="emdg-btn float-right" onClick={(e) => this.saveAndClose()}>Complete row</Button>
            <Button  className="emdg-btn emdg-btn--secondary float-left mr-2" onClick={this.props.onClose}>Cancel</Button>
            <Button  className="emdg-btn emdg-btn--secondary float-left" onClick={this.props.onRemove} style={{ display: this.props.model ? '' : 'none' }}>Remove</Button>

          </ShowIf>

          <Backdrop on={this.state.loading} />
        </>
      )
    }
}
